import { gql, useApolloClient, useMutation } from '@apollo/client';
import {
  ButtonGroup,
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalOverlay,
} from '@apollo/orbit';
import { useFormik } from 'formik';
import { AnimatePresence } from 'framer-motion';
import React, { useCallback, useMemo, useState } from 'react';
import { useHistory } from 'react-router';
import { Wizard } from 'react-use-wizard';

import {
  HeaderEntry,
  INITIAL_HEADERS,
} from 'src/app/graph/explorerPage/hooks/useExplorerState/useHeadersManagerContext/shared';
import { GraphRef } from 'src/app/graph/hooks/useGraphRef';
import { AddSubgraphFormMutation } from 'src/app/graph/shared/addSubgraphForm/AddSubgraphForm';
import { ErrorMessage } from 'src/components/errorMessage/ErrorMessage';
import { WizardHeader } from 'src/components/wizard/WizardHeader';
import { useCurrentAccountId } from 'src/hooks/useCurrentAccountId';
import { GraphQLTypes } from 'src/lib/graphqlTypes';
import { setOverlayClosedLocation } from 'src/lib/routing';

import { AddSchemaStep } from './AddSchemaStep';
import { AddVariantWizardControls } from './AddVariantWizardControls';
import { NameVariantStep } from './NameVariantStep';
import {
  AddVariantWizardFields,
  AddVariantWizardSteps,
  addVariantWizardValidationSchema,
} from './shared';

const AddNewVariantMutation = gql`
  mutation AddNewVariantMutation(
    $variant: String!
    $graphId: ID!
    $uploadSchemaSchemaDocument: String!
  ) {
    service(id: $graphId) {
      uploadSchema(tag: $variant, schemaDocument: $uploadSchemaSchemaDocument) {
        success
      }
    }
  }
`;

export const AddVariantWizard = ({
  onClose,
  isOpen,
  graphRef,
  startIndex = 0,
  allVariants,
  isCloudSupergraph,
  hasManagedSubgraphs,
}: {
  onClose: () => void;
  isOpen: boolean;
  graphRef: GraphRef;
  startIndex?: number;
  allVariants: string[];
  isCloudSupergraph: boolean;
  hasManagedSubgraphs: boolean;
}) => {
  const [currentAccountId] = useCurrentAccountId();

  const client = useApolloClient();

  const [addSubgraph, { error: addSubgraphError }] = useMutation<
    GraphQLTypes.AddSubgraphFormMutation,
    GraphQLTypes.AddSubgraphFormMutationVariables
  >(AddSubgraphFormMutation);
  const history = useHistory();

  const [addNewVariant, { error: addNewVariantError }] = useMutation<
    GraphQLTypes.AddNewVariantMutation,
    GraphQLTypes.AddNewVariantMutationVariables
  >(AddNewVariantMutation);

  const mutationError = addSubgraphError ?? addNewVariantError;

  const { resetForm, isSubmitting, ...formProps } =
    useFormik<AddVariantWizardFields>({
      initialValues: {
        variantName: '',
        sdl: '',
        routingUrl: '',
        subgraphName: '',
        localEndpointUrl: '',
        isFederated: hasManagedSubgraphs,
      },
      validateOnBlur: true,
      validateOnChange: false,
      validationSchema: addVariantWizardValidationSchema,
      onSubmit: async (submittedFormValues) => {
        if (!graphRef) return;
        formProps.setSubmitting(true);
        const encodedRoutingUrl = new URL(
          submittedFormValues.routingUrl,
        ).toString();

        try {
          let responseSuccess = false;
          if (hasManagedSubgraphs) {
            const publishSubgraphResponse = await addSubgraph({
              variables: {
                graphId: graphRef.graphId,
                variant: formProps.values.variantName,
                subgraphName: submittedFormValues.subgraphName,
                routingUrl: encodedRoutingUrl,
                revision: `studio-${currentAccountId}-${Date.now()}`,
                partialSchema: {
                  sdl: submittedFormValues.sdl,
                },
              },
            });
            responseSuccess = !publishSubgraphResponse.errors;
          } else {
            const publishVariantResponse = await addNewVariant({
              variables: {
                graphId: graphRef.graphId,
                variant: formProps.values.variantName,
                uploadSchemaSchemaDocument: submittedFormValues.sdl,
              },
            });
            responseSuccess =
              !!publishVariantResponse.data?.service?.uploadSchema?.success;
          }
          if (responseSuccess) {
            client.cache.evict({
              id: client.cache.identify({
                __typename: 'Service',
                id: graphRef.graphId,
              }),
              fieldName: 'variant',
            });

            history.replace({
              ...setOverlayClosedLocation({ ...history.location }),
              state: {
                ...(typeof history.location.state === 'object'
                  ? history.location.state
                  : {}),
                highlightVariantState: {
                  variant: submittedFormValues.variantName,
                },
              },
            });
            resetForm({});
          }
        } catch {
          // we handle the error via mutationError
        }
        formProps.setSubmitting(false);
      },
    });

  const variantNameAvailable = React.useMemo(
    () => !allVariants.includes(formProps.values.variantName),
    [allVariants, formProps.values.variantName],
  );

  const onCloseModal = useCallback(() => {
    onClose();
    resetForm({});
  }, [onClose, resetForm]);

  /**
   * Add Subgraph State stored in the parent so that
   * when user presses back the step is not reset
   */
  const [advancedOptionsDrawerOpen, setAdvancedOptionsDrawerOpen] =
    useState(false);
  const [advancedOptionsTab, setAdvancedOptionsTab] = useState<
    'headers' | 'schemaFile' | 'localIntrospection'
  >('headers');

  const [schemaUploaded, setSchemaUploaded] = useState(false);
  const [isUsingLocalIntrospection, setIsUsingLocalIntrospection] =
    useState(false);
  const [headers, setHeaders] = useState<HeaderEntry[]>(INITIAL_HEADERS);

  const [currentStep, setCurrentStep] = useState(0);

  const steps = useMemo(
    () => AddVariantWizardSteps(hasManagedSubgraphs),
    [hasManagedSubgraphs],
  );

  return (
    <Modal
      isOpen={isOpen}
      size={currentStep === 0 ? 'xl' : '2xl'}
      onClose={onCloseModal}
      scrollBehavior="inside"
      // cannot close modal when submitting
      closeOnEsc={!isSubmitting}
      closeOnOverlayClick={!isSubmitting}
    >
      <ModalOverlay />
      <ModalContent>
        {mutationError && (
          <ErrorMessage>
            Something went wrong making a new variant, please refresh the page
            and try again
          </ErrorMessage>
        )}

        <Wizard
          startIndex={startIndex}
          header={
            <WizardHeader
              animated={false}
              wizardSteps={steps}
              className="mx-6 mb-4 mt-6 rounded-lg border border-solid border-silver-darker bg-silver-lighter px-10"
            />
          }
          wrapper={<WizardWrapper />}
          footer={
            <ModalFooter>
              {currentStep === 0 ? (
                <ButtonGroup>
                  <AddVariantWizardControls.CancelButton
                    onClose={onCloseModal}
                    addVariantWizardSteps={steps}
                    disabled={isSubmitting}
                  />
                  <AddVariantWizardControls.ContinueButton
                    disabled={
                      formProps.values.variantName.length === 0 ||
                      !!formProps.errors?.variantName ||
                      !variantNameAvailable
                    }
                    disabledReason={formProps.errors?.variantName}
                  />
                </ButtonGroup>
              ) : (
                <>
                  <AddVariantWizardControls.CancelButton
                    onClose={onClose}
                    addVariantWizardSteps={steps}
                    disabled={isSubmitting}
                  />
                  <ButtonGroup>
                    <AddVariantWizardControls.BackButton
                      addVariantWizardSteps={steps}
                      disabled={isSubmitting}
                    />
                    <AddVariantWizardControls.SubmitButton
                      {...formProps}
                      isSubmitting={isSubmitting}
                      subgraphNameAvailable={true}
                    />
                  </ButtonGroup>
                </>
              )}
            </ModalFooter>
          }
        >
          <NameVariantStep
            {...formProps}
            setCurrentStep={setCurrentStep}
            variantNameAvailable={variantNameAvailable}
            isCloudSupergraph={isCloudSupergraph}
          />
          <AddSchemaStep
            setCurrentStep={setCurrentStep}
            graphRef={graphRef}
            // first subgraph on new variant will always be availabe
            subgraphNameAvailable={true}
            // a new variant has no other subgraphs so composition errors not possible
            shouldShowCompositionErrorsBanner={false}
            urlPlaceholder="Add your GraphQL endpoint"
            isSubmitting={isSubmitting}
            {...formProps}
            onClose={onCloseModal}
            advancedOptionsDrawerOpen={advancedOptionsDrawerOpen}
            advancedOptionsTab={advancedOptionsTab}
            headers={headers}
            schemaUploaded={schemaUploaded}
            isUsingLocalIntrospection={isUsingLocalIntrospection}
            setAdvancedOptionsDrawerOpen={setAdvancedOptionsDrawerOpen}
            setAdvancedOptionsTab={setAdvancedOptionsTab}
            setHeaders={setHeaders}
            setIsUsingLocalIntrospection={setIsUsingLocalIntrospection}
            setSchemaUploaded={setSchemaUploaded}
            hasManagedSubgraphs={hasManagedSubgraphs}
          />
        </Wizard>
      </ModalContent>
    </Modal>
  );
};

const WizardWrapper = ({ children }: { children?: React.ReactNode }) => (
  <ModalBody>
    <AnimatePresence initial={false} exitBeforeEnter>
      {children}
    </AnimatePresence>
  </ModalBody>
);
